import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import { INSIGHTS_KEY } from './use-insights';

import type {
  InsightActionRaw,
  InsightActionType,
  InsightFalseFeedbackReason,
  InsightGenerationJobRaw,
  InsightLens,
  InsightRaw,
  SensitivityMode,
} from '../types';
import type { UseMutationResult } from '@tanstack/react-query';

// Mutations de decisão da Tela 10. Cada sucesso invalida toda a árvore de insights
// (lista pendentes + histórico + métricas).
function useInvalidateInsights(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: INSIGHTS_KEY });
  };
}

export function useAcceptInsight(): UseMutationResult<InsightRaw, Error, { id: string }> {
  const invalidate = useInvalidateInsights();
  return useMutation({
    mutationFn: ({ id }) => api.acceptInsight(id),
    onSuccess: invalidate,
  });
}

export function useFalsePositiveInsight(): UseMutationResult<
  InsightRaw,
  Error,
  { id: string; reason: InsightFalseFeedbackReason; feedbackText?: string }
> {
  const invalidate = useInvalidateInsights();
  return useMutation({
    mutationFn: ({ id, reason, feedbackText }) =>
      api.falsePositiveInsight(id, {
        reason,
        ...(feedbackText !== undefined ? { feedbackText } : {}),
      }),
    onSuccess: invalidate,
  });
}

export function useTakeInsightAction(): UseMutationResult<
  InsightActionRaw,
  Error,
  { id: string; actionType: InsightActionType; payload: Record<string, unknown> }
> {
  const invalidate = useInvalidateInsights();
  return useMutation({
    mutationFn: ({ id, actionType, payload }) => api.takeInsightAction(id, { actionType, payload }),
    onSuccess: invalidate,
  });
}

export function useArchiveInsight(): UseMutationResult<
  InsightRaw,
  Error,
  { id: string; reasonNote?: string }
> {
  const invalidate = useInvalidateInsights();
  return useMutation({
    mutationFn: ({ id, reasonNote }) =>
      api.archiveInsight(id, reasonNote !== undefined ? { reasonNote } : {}),
    onSuccess: invalidate,
  });
}

export function useTriggerGeneration(
  lens: InsightLens = 'supervisor',
): UseMutationResult<{ jobId: string }, Error, void> {
  return useMutation({
    mutationFn: () => api.triggerInsightGeneration(lens),
  });
}

export function useSetSensitivity(): UseMutationResult<
  { mode: SensitivityMode },
  Error,
  { mode: SensitivityMode }
> {
  return useMutation({
    mutationFn: ({ mode }) => api.setInsightSensitivity(mode),
  });
}

/**
 * Poll do job de geração manual (D7). Refaz fetch a cada 1.5s enquanto pending/running.
 * Ao completar (completed|failed), invalida a árvore de insights (a lista pode ter mudado).
 */
export function useGenerationJobPoll(jobId: string | null): {
  job: InsightGenerationJobRaw | undefined;
  isDone: boolean;
} {
  const invalidate = useInvalidateInsights();
  const q = useQuery({
    queryKey: [...INSIGHTS_KEY, 'job', jobId],
    queryFn: async () => {
      const job = await api.getInsightGenerationJob(jobId!);
      if (job.status === 'completed' || job.status === 'failed') invalidate();
      return job;
    },
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'completed' || s === 'failed' ? false : 1500;
    },
  });
  const isDone = q.data?.status === 'completed' || q.data?.status === 'failed';
  return { job: q.data, isDone };
}
